/**
 * Advanced System Capture (Feature 11)
 * Parses the browser environment to populate browser, OS, device, and network metadata.
 */

export interface BrowserInfo {
  name: string;
  version: string;
  engine: string;
}

export interface OSInfo {
  name: string;
  version: string;
  platform: string;
}

export interface DeviceInfo {
  type: 'desktop' | 'mobile' | 'tablet';
  isMobile: boolean;
  isTablet: boolean;
  brand?: string;
  model?: string;
}

export interface NetworkInfo {
  effectiveType: string;
  rtt: number;
  downlink: number;
  saveData: boolean;
}

export interface SystemCapture {
  browser: BrowserInfo;
  os: OSInfo;
  device: DeviceInfo;
  network?: NetworkInfo;
}

export function captureSystemInfo(): SystemCapture {
  const ua = navigator.userAgent;

  return {
    browser: parseBrowser(ua),
    os: parseOS(ua),
    device: parseDevice(ua),
    network: parseNetwork(),
  };
}

function parseBrowser(ua: string): BrowserInfo {
  // Order matters — check more specific browsers first
  if (/Edg\/(\d[\d.]*)/i.test(ua)) {
    return { name: 'Edge', version: RegExp.$1, engine: 'Blink' };
  }
  if (/OPR\/(\d[\d.]*)/i.test(ua)) {
    return { name: 'Opera', version: RegExp.$1, engine: 'Blink' };
  }
  if (/Chrome\/(\d[\d.]*)/i.test(ua) && !/Edg/i.test(ua)) {
    return { name: 'Chrome', version: RegExp.$1, engine: 'Blink' };
  }
  if (/Firefox\/(\d[\d.]*)/i.test(ua)) {
    return { name: 'Firefox', version: RegExp.$1, engine: 'Gecko' };
  }
  if (/Version\/(\d[\d.]*).*Safari/i.test(ua)) {
    return { name: 'Safari', version: RegExp.$1, engine: 'WebKit' };
  }
  return { name: 'Unknown', version: '', engine: 'Unknown' };
}

function parseOS(ua: string): OSInfo {
  const platform = (navigator as any).userAgentData?.platform
    ?? navigator.platform
    ?? '';

  if (/Windows NT (\d[\d.]*)/i.test(ua)) {
    const ver = RegExp.$1;
    const friendly = ver === '10.0' ? '10+' : ver;
    return { name: 'Windows', version: friendly, platform };
  }
  if (/Mac OS X (\d[\d_]*)/i.test(ua)) {
    return { name: 'macOS', version: RegExp.$1.replace(/_/g, '.'), platform };
  }
  if (/iPhone OS (\d[\d_]*)/i.test(ua)) {
    return { name: 'iOS', version: RegExp.$1.replace(/_/g, '.'), platform };
  }
  if (/Android (\d[\d.]*)/i.test(ua)) {
    return { name: 'Android', version: RegExp.$1, platform };
  }
  if (/CrOS/i.test(ua)) {
    return { name: 'ChromeOS', version: '', platform };
  }
  if (/Linux/i.test(ua)) {
    return { name: 'Linux', version: '', platform };
  }
  return { name: 'Unknown', version: '', platform };
}

function parseDevice(ua: string): DeviceInfo {
  const isMobile = /Mobi|Android.*Mobile|iPhone|iPod/i.test(ua);
  const isTablet = !isMobile && /iPad|Android(?!.*Mobile)|Tablet/i.test(ua);
  const type: DeviceInfo['type'] = isMobile ? 'mobile' : isTablet ? 'tablet' : 'desktop';

  let brand: string | undefined;
  let model: string | undefined;

  if (/iPhone/i.test(ua)) {
    brand = 'Apple';
    model = 'iPhone';
  } else if (/iPad/i.test(ua)) {
    brand = 'Apple';
    model = 'iPad';
  } else if (/Samsung/i.test(ua)) {
    brand = 'Samsung';
    const match = ua.match(/SM-[\w]+/i);
    if (match) model = match[0];
  } else if (/Pixel/i.test(ua)) {
    brand = 'Google';
    const match = ua.match(/Pixel[\w\s]*/i);
    if (match) model = match[0].trim();
  }

  return { type, isMobile, isTablet, brand, model };
}

function parseNetwork(): NetworkInfo | undefined {
  const conn = (navigator as any).connection;
  if (!conn) return undefined;

  return {
    effectiveType: conn.effectiveType ?? 'unknown',
    rtt: conn.rtt ?? 0,
    downlink: conn.downlink ?? 0,
    saveData: conn.saveData ?? false,
  };
}
